<template>
  <v-container fluid class="payment-page pa-0">
    <Create :CustomerId="CustomerId" :DonorId="DonorId" :createDialog="createDialog" @close="createDialog = false" @submit="getList()">
    </Create>
    <v-card :loading="loading" outlined class="payment-card">
      <div class="payment-header">
        <div>
          <div class="section-eyebrow">PAYMENT HISTORY</div>
          <h2>Payments</h2>
          <p>Review received payments and the invoices where each payment was applied.</p>
        </div>
        <v-btn v-if="canAddPayment" color="primary" depressed class="payment-add" @click="add">
          <v-icon left>mdi-plus</v-icon>
          New payment
        </v-btn>
      </div>

      <div class="payment-summary">
        <div class="summary-item">
          <span class="summary-value">{{ payments.length }}</span>
          <span class="summary-label">Payments in period</span>
        </div>
        <div class="summary-item">
          <span class="summary-value">{{ fullTotal | currency }}</span>
          <span class="summary-label">Total received</span>
        </div>
        <div v-if="search" class="summary-item">
          <span class="summary-value">{{ total | currency }}</span>
          <span class="summary-label">Filtered total</span>
        </div>
      </div>

      <div class="payment-filters">
        <v-text-field v-model="search" outlined prepend-inner-icon="mdi-magnify" dense
          label="Search payments" placeholder="Number, note, or creator" single-line hide-details clearable />
        <div v-if="!showAll" class="date-filter">
          <DateTimePicker defaultRange="thisYear" :range="true" v-model="dateTime" @submit="getList()" />
        </div>
      </div>

      <v-data-table class="payment-table" :headers="headers" :items="payments" :search="search" :loading="loading"
          @current-items="updateTotal" :sort-by="sortBy" sort-desc :page.sync="page" :expanded.sync="expanded"
          show-expand :items-per-page="itemsPerPage">
          <template v-slot:item="{ item, isExpanded, expand }">
            <tr class="payment-row">
              <td class="expand-cell" data-label="Details">
                <v-btn :aria-label="isExpanded ? 'Hide payment details' : 'Show payment details'"
                  @click="expand(!isExpanded)" icon small color="primary">
                  <v-icon>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>
              </td>
              <td class="text-left" data-label="Date received">
                <div class="cell-primary">{{ item.date | moment("MMM D, YYYY") }}</div>
                <div class="cell-secondary">{{ item.date | moment("h:mm A") }}</div>
              </td>
              <td class="text-left" data-label="Created by">
                <span class="cell-primary">{{ item.User ? item.User.name : "Not recorded" }}</span>
              </td>
              <td class="text-left" data-label="Payment number">
                <div class="d-flex align-center flex-wrap">
                  <span class="payment-number">#{{ item.localId }}</span>
                  <v-chip color="green lighten-5" text-color="green darken-2" x-small label class="ml-2 font-weight-bold"
                    v-if="item.donation">Donation</v-chip>
                </div>
              </td>
              <td class="text-left" data-label="Customer" v-if="!CustomerId && !DonorId">
                <span class="cell-primary">{{ item.Customer ? item.Customer.name : "—" }}</span>
              </td>
              <td class="text-left note-cell" data-label="Note">
                <span v-if="item.note">{{ item.note }}</span>
                <span v-else class="cell-secondary">No note</span>
              </td>
              <td class="text-right payment-amount" data-label="Amount">
                {{ item.total | currency }}
              </td>
              <td class="justify-end align-center text-right action-cell" data-label="Actions">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon aria-label="Payment actions">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="menu-button">
                    <v-list-item v-if="!CustomerId && !DonorId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>
                    <v-divider v-if="!CustomerId && !DonorId"></v-divider>
                    <v-list-item @click="viewpayment(item)">
                      <v-list-item-title>View payment</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0 expanded-cell">
              <div class="expanded-payment">
                <div class="expanded-heading">
                  <v-icon color="primary" small class="mr-2">mdi-file-document-multiple-outline</v-icon>
                  Payment allocation for #{{ item.localId }}
                </div>
                <div class="expanded-content">
                  <PaymentDetail :PaymentId="item.id" :CustomerId="CustomerId" :DonorId="DonorId"></PaymentDetail>
                </div>
              </div>
            </td>
          </template>
          <template v-slot:no-data>
            <div class="empty-payments py-10">
              <v-avatar color="grey lighten-4" size="52" class="mb-3"><v-icon color="grey">mdi-cash-remove</v-icon></v-avatar>
              <div class="font-weight-bold">No payments found</div>
              <div class="text-body-2 text--secondary">Payments for this period will appear here.</div>
            </div>
          </template>
          <template v-slot:no-results>
            <div class="empty-payments py-10">
              <div class="font-weight-bold">No matching payments</div>
              <div class="text-body-2 text--secondary">Try a different payment number, note, or creator.</div>
            </div>
          </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import paymentService from "../service";
const DateTimePicker = () => import("@/components/DateTimePicker");
import PaymentDetail from "./PaymentDetailList";
import { mapGetters } from "vuex";

const Create = () => import("./Create");

export default {
  props: {
    CustomerId: {
      default: null,
    },
    DonorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },
  name: "payment-list",
  data() {
    return {
      fullTotal: 0,
      total: 0,
      expanded: [],
      singleExpand: true,
      dateTime: {
        start: this.$moment()
          .startOf("year")
          .toISOString(),
        end: this.$moment()
          .endOf("year")
          .toISOString(),
      },
      createDialog: false,
      selectedPayee: null,
      headers: [],
      payments: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "localId",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    // this.getList();
    this.loadHeader();
    this.$events.listen("paymentListEvent", () => this.getList());
  },
  beforeDestroy() {
    this.$events.remove("paymentListEvent");
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    canAddPayment() {
      return this.checkRightStatus(31) && Boolean(this.CustomerId || this.DonorId);
    },
  },
  watch: {
    CustomerId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
  },
  components: {
    DateTimePicker,
    Create,
    PaymentDetail,
  },
  methods: {
    updateTotal(val) {
      this.total = 0;
      val.map((row) => {
        this.total += row.total;
      });
    },
    add() {
      this.createDialog = !this.createDialog;
    },
    viewProfile(val) {
      // console.log("val", val);
      this.$router.push({ path: `/customer/${val.CustomerId}/profile` });
    },
    viewpayment(payment) {
      console.log('payment', payment)
      const routeData = this.$router.resolve({
        path: `/global/payment/${payment.id}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    viewPackingSlip(payment) {
      const routeData = this.$router.resolve({
        path: `/global/payment/${payment.id}/packingSlip`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    loadHeader() {
      this.headers = [];
      this.headers.push({
        text: this.$t("labels.date"),
        align: "left",
        value: "date",
        sortable: true,
      },
      {
        text: "Created By",
        align: "left",
        value: "User.name",
        sortable: true,
      });

      // this.headers.push({
      //   text: "User Name",
      //   align: "left",
      //   value: "date",
      //   sortable: true,
      // });

      this.headers.push({
        text: "Payment Number",
        align: "left",
        value: "localId",
        sortable: true,
      });

      if (!this.CustomerId && !this.DonorId) {
        this.headers.push({
          text: "Customer Name",
          value: "Customer.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push({
        text: "Note",
        align: "left",
        value: "note",
        sortable: true,
      });

      this.headers.push({
        text: "Total",
        value: "total",
        align: "right",
        sortable: true,
      });

      // this.headers.push({
      //   text: "Payment",
      //   value: "payment",
      //   align: "right",
      //   sortable: true
      // });

      this.headers.push({
        text: "",
        value: "action",
        sortable: false,
      });
    },
    getList() {
      this.loading = true;
      let where = {};
      if (!this.showAll) {
        where = {
          start: this.dateTime.start,
          end: this.dateTime.end,
        };
      }
      if (this.DonorId) {
        where.DonorId = this.DonorId;
      }
      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      return paymentService.getAll(where).then((response) => {
        console.log('get all payment response', response)
        this.loading = false;
        this.payments = response.data;

        this.payments.map(payment => {
          payment.total = 0
          payment.PaymentDetails.map(paymentDetail => {
            payment.total = payment.total + paymentDetail.amount
          })
        })
        this.fullTotal = 0;
        this.payments.map((row) => {
          this.fullTotal += row.total;
        });
        return response;
      });
    },
  },
};
</script>

<style scoped>
.payment-card {
  border-color: #e1e8e1 !important;
  border-radius: 14px !important;
  overflow: hidden;
}

.payment-header {
  padding: 24px 26px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.section-eyebrow {
  margin-bottom: 5px;
  color: #43a047;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.payment-header h2 {
  margin: 0;
  color: #172033;
  font-size: 1.55rem;
}

.payment-header p {
  margin: 5px 0 0;
  color: #829087;
}

.payment-add {
  border-radius: 8px;
}

.payment-summary {
  display: flex;
  gap: 42px;
  padding: 17px 26px;
  background: #f7faf7;
  border-top: 1px solid #e7ece7;
  border-bottom: 1px solid #e7ece7;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-value {
  color: #172033;
  font-size: 1.2rem;
  font-weight: 800;
}

.summary-label {
  color: #829087;
  font-size: 0.75rem;
}

.payment-filters {
  padding: 18px 26px;
  display: grid;
  grid-template-columns: minmax(260px, 460px) minmax(240px, 330px);
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid #e7ece7;
}

.cell-primary,
.payment-number,
.payment-amount {
  color: #172033;
  font-weight: 700;
}

.cell-secondary {
  color: #98a19b;
  font-size: 0.75rem;
}

.note-cell {
  max-width: 260px;
}

.expanded-payment {
  padding: 18px 22px 22px;
  background: #f5f9f5;
  border-top: 1px solid #dfe8df;
  border-bottom: 1px solid #dfe8df;
}

.expanded-heading {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  color: #526057;
  font-size: 0.82rem;
  font-weight: 700;
}

.empty-payments {
  color: #526057;
  text-align: center;
}

@media (max-width: 700px) {
  .payment-header {
    padding: 20px 18px 16px;
    flex-direction: column;
  }

  .payment-add {
    width: 100%;
  }

  .payment-summary {
    gap: 24px;
    padding: 14px 18px;
    overflow-x: auto;
  }

  .summary-item {
    flex: 0 0 auto;
  }

  .payment-filters {
    padding: 16px 18px;
    grid-template-columns: 1fr;
  }

  .payment-table >>> thead {
    display: none;
  }

  .payment-table >>> tbody tr.payment-row {
    margin: 12px;
    padding: 14px;
    display: grid;
    grid-template-columns: 1fr auto;
    border: 1px solid #e1e8e1;
    border-radius: 10px;
  }

  .payment-table >>> tbody tr.payment-row td {
    height: auto;
    padding: 8px 4px;
    display: block;
    border: 0 !important;
  }

  .payment-table >>> tbody tr.payment-row td::before {
    content: attr(data-label);
    display: block;
    color: #8a958d;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .payment-table >>> .expand-cell,
  .payment-table >>> .action-cell {
    grid-column: 2;
    grid-row: 1;
  }

  .payment-table >>> .action-cell {
    margin-top: 40px;
  }

  .payment-table >>> .payment-amount {
    font-size: 1.08rem;
  }

  .payment-table >>> .expanded-cell {
    display: table-cell !important;
  }
}
</style>
